// ============================================================================
// ITEM CLASSIFICATIONS (ADR-0058 slice 1) — tenant-defined ways of grouping
// items (Brand, Division, Sub-category…). Each classification (dimension)
// carries its own list of values; a hierarchical one lets values nest.
// The dimension and its values save together: the update sends the FULL value
// set (existing rows keep their id, new rows have none, parent named by code).
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useClassifications } from 'hooks/api';
import { classificationService } from 'api/services';
import type {
  ClassificationDimensionDto, ClassificationDimensionQueryParams,
  CreateClassificationDimensionRequest, ClassificationValueInput,
} from 'api/types/inventory.types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type ApiError = { response?: { status?: number; data?: { message?: string } } };
const errorMessage = (err: unknown, fallback: string) => (err as ApiError)?.response?.data?.message || fallback;
const errorStatus = (err: unknown) => (err as ApiError)?.response?.status;

type IconComponent = React.ComponentType<{ size?: number }>;
const ICONS = IconSystem as unknown as Record<string, IconComponent | undefined>;

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const yesNo = (b: boolean) => (b ? 'Yes' : 'No');

/** "Managed by <module>": the classification is maintained by a domain module (read-only here). */
const OwnerBadge: React.FC<{ module: string }> = ({ module }) => (
  <span className="badge" title="Maintained by a domain module; read-only here" style={{
    marginLeft: '0.4rem', fontSize: '0.68rem', fontWeight: 600, verticalAlign: 'middle',
    background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)',
  }}>
    Managed by {module}
  </span>
);

// ---------------------------------------------------------------------------
// Form model
// ---------------------------------------------------------------------------

/** One editable value row. `key` is client-only; parent is held by row key so renaming a code keeps the link. */
interface ValueRow {
  key: string;
  id: string | null;
  valueCode: string;
  valueName: string;
  parentKey: string;
  displayOrder: string;
  itemCount: number;
  /** Values of dependent classifications allowed under this one (blocks removal while > 0). */
  dependentLinkCount: number;
  /** Depends-on classification only: parent-classification value ids; empty = any. */
  allowedUnder: string[];
}

interface FormData {
  dimensionCode: string;
  dimensionName: string;
  isHierarchical: boolean;
  isRequired: boolean;
  leafOnly: boolean;
  dependsOnDimensionId: string;
  displayOrder: string;
  description: string;
  statusCode: string;
  values: ValueRow[];
}

const EMPTY: FormData = {
  dimensionCode: '', dimensionName: '', isHierarchical: false, isRequired: false,
  leafOnly: false, dependsOnDimensionId: '',
  displayOrder: '0', description: '', statusCode: 'ACTIVE', values: [],
};

const hintText: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' };

let newRowSeq = 0;
const newRowKey = () => `new-${++newRowSeq}`;

const buildForm = (d: ClassificationDimensionDto, mode: 'edit' | 'copy'): FormData => {
  const isCopy = mode === 'copy';
  const keyById = new Map<string, string>();
  const keyByCode = new Map<string, string>();
  const rows: ValueRow[] = (d.values || []).map(v => {
    const key = isCopy ? newRowKey() : v.id;
    keyById.set(v.id, key);
    keyByCode.set(v.valueCode.toUpperCase(), key);
    return {
      key, id: isCopy ? null : v.id,
      valueCode: v.valueCode, valueName: v.valueName, parentKey: '',
      displayOrder: String(v.displayOrder ?? 0), itemCount: isCopy ? 0 : (v.itemCount ?? 0),
      dependentLinkCount: isCopy ? 0 : (v.dependentLinkCount ?? 0),
      allowedUnder: (v.allowedUnder || []).map(a => a.valueId),
    };
  });
  (d.values || []).forEach((v, i) => {
    const parentKey = (v.parentId && keyById.get(v.parentId))
      || (v.parentCode && keyByCode.get(v.parentCode.toUpperCase()))
      || '';
    rows[i].parentKey = parentKey;
  });
  return {
    dimensionCode: isCopy ? '' : d.dimensionCode,
    dimensionName: isCopy ? `${d.dimensionName} (Copy)` : d.dimensionName,
    isHierarchical: d.isHierarchical,
    isRequired: d.isRequired,
    leafOnly: d.isHierarchical && !!d.leafOnly,
    dependsOnDimensionId: d.dependsOnDimensionId || '',
    displayOrder: String(d.displayOrder ?? 0),
    description: d.description || '',
    statusCode: d.statusCode || 'ACTIVE',
    values: rows,
  };
};

const toInt = (s: string) => {
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : 0;
};

const LoadingOverlay: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
  }}>
    <div style={{
      width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
      borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{text}</span>
    <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
  </div>
);

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '0.45rem 0.5rem', fontSize: '0.75rem', fontWeight: 600,
  color: 'var(--text-secondary)', background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)', whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = {
  padding: '0.35rem 0.5rem', borderBottom: '1px solid var(--border-color)', verticalAlign: 'top',
  color: 'var(--text-primary)',
};
const cellInput: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.3rem 0.5rem' };

// ---------------------------------------------------------------------------
// FORM MODAL
// ---------------------------------------------------------------------------

type FormMode = 'create' | 'edit' | 'copy';

interface ClassificationFormModalProps {
  isOpen: boolean;
  mode: FormMode;
  record: ClassificationDimensionDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
  onRestoreModal?: (mode: FormMode, instanceId?: string, recordCode?: string) => void;
}

const ClassificationFormModal: React.FC<ClassificationFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const [form, setForm] = useState<FormData>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [flatConfirmOpen, setFlatConfirmOpen] = useState(false);
  // Depends on: candidate classifications, the chosen one's values, and a pending change awaiting confirm.
  const [dimOptions, setDimOptions] = useState<SelectOption[]>([]);
  const [parentValueOptions, setParentValueOptions] = useState<SelectOption[]>([]);
  const [loadingParentValues, setLoadingParentValues] = useState(false);
  const [pendingDependsOn, setPendingDependsOn] = useState<string | null>(null);

  const formRef = useRef(form); formRef.current = form;
  const modeRef = useRef(mode); modeRef.current = mode;
  const recordRef = useRef(record); recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const detailRef = useRef<ClassificationDimensionDto | null>(null);
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    setFieldErrors({});
    const r = recordRef.current;
    const m = modeRef.current;
    if (!r || m === 'create') {
      detailRef.current = null;
      const data: FormData = { ...EMPTY, values: [] };
      setForm(data);
      initialSnapshotRef.current = JSON.stringify(data);
      setLoadingDetail(false);
      return;
    }
    // List rows don't carry values — load the full dimension.
    let cancelled = false;
    initialSnapshotRef.current = '';
    setLoadingDetail(true);
    classificationService.getById(r.id)
      .then(detail => {
        if (cancelled) return;
        detailRef.current = detail;
        const data = buildForm(detail, m === 'copy' ? 'copy' : 'edit');
        setForm(data);
        initialSnapshotRef.current = JSON.stringify(data);
      })
      .catch(err => {
        if (cancelled) return;
        notificationService.toast.error(errorMessage(err, 'Could not load the classification.'));
        onClose();
      })
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Candidate "Depends on" classifications: other ACTIVE ones (never the one being edited).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const selfId = modeRef.current === 'edit' ? recordRef.current?.id : undefined;
    classificationService.getAll({ pageSize: 100 })
      .then(res => {
        if (cancelled) return;
        setDimOptions(res.data
          .filter(d => d.id !== selfId && (d.statusCode || 'ACTIVE') === 'ACTIVE')
          .map(d => ({ value: d.id, label: `${d.dimensionCode} - ${d.dimensionName}` })));
      })
      .catch(() => { if (!cancelled) setDimOptions([]); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // The chosen parent classification's values feed each row's "Allowed under" picker.
  const dependsOnId = form.dependsOnDimensionId;
  useEffect(() => {
    if (!isOpen || !dependsOnId) { setParentValueOptions([]); setLoadingParentValues(false); return; }
    let cancelled = false;
    setLoadingParentValues(true);
    classificationService.getById(dependsOnId)
      .then(parent => {
        if (cancelled) return;
        setParentValueOptions([...(parent.values || [])]
          .sort((a, b) => a.displayOrder - b.displayOrder || a.valueCode.localeCompare(b.valueCode))
          .map(v => ({ value: v.id, label: `${v.valueCode} - ${v.valueName}` })));
      })
      .catch(err => {
        if (cancelled) return;
        setParentValueOptions([]);
        notificationService.toast.error(errorMessage(err, 'Could not load the values of the classification this one depends on.'));
      })
      .finally(() => { if (!cancelled) setLoadingParentValues(false); });
    return () => { cancelled = true; };
  }, [isOpen, dependsOnId]);

  // Keep an inactive/out-of-list current choice visible by its saved name.
  const dependsOnOptions = useMemo<SelectOption[]>(() => {
    const id = form.dependsOnDimensionId;
    if (!id || dimOptions.some(o => o.value === id)) return dimOptions;
    const name = detailRef.current?.dependsOnDimensionId === id ? detailRef.current?.dependsOnDimensionName : null;
    return [...dimOptions, { value: id, label: name || 'Current classification' }];
  }, [dimOptions, form.dependsOnDimensionId]);

  const dependsOnName = useMemo(() => {
    const label = dependsOnOptions.find(o => o.value === form.dependsOnDimensionId)?.label || '';
    const sep = label.indexOf(' - ');
    return sep >= 0 ? label.slice(sep + 3) : label;
  }, [dependsOnOptions, form.dependsOnDimensionId]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(form) !== initialSnapshotRef.current;
  }, [form]);

  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(prev => {
      if (!prev[f]) return prev;
      const next = { ...prev }; delete next[f]; return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentMode = modeRef.current;
    const currentInstanceId = createInstanceIdRef.current || '1';
    const currentCode = recordRef.current?.dimensionCode;
    const savedForm: FormData = { ...formRef.current, values: formRef.current.values.map(v => ({ ...v })) };
    const savedSnapshot = initialSnapshotRef.current;
    const savedDetail = detailRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(savedForm) !== savedSnapshot : false;

    const modalId = currentMode === 'create'
      ? `${tabId}-form-modal-create-${currentInstanceId}`
      : `${tabId}-form-modal-${currentMode}-${currentCode}`;

    const label = savedForm.dimensionCode.trim() || savedForm.dimensionName.trim();
    const title = currentMode === 'create'
      ? (label ? `Add - ${label} (${currentInstanceId})` : `Add New (${currentInstanceId})`)
      : currentMode === 'copy'
        ? `Copy - ${currentCode}`
        : `Edit - ${currentCode}`;

    minimize({
      id: modalId, title, moduleName, tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        detailRef.current = savedDetail;
        setForm(savedForm);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          currentMode,
          currentMode === 'create' ? currentInstanceId : undefined,
          currentMode !== 'create' ? currentCode : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Value rows ---------------------------------------------------------

  const updateRow = useCallback((key: string, patch: Partial<ValueRow>) => {
    setForm(p => ({ ...p, values: p.values.map(v => (v.key === key ? { ...v, ...patch } : v)) }));
  }, []);

  const addRow = useCallback(() => {
    setForm(p => {
      const nextOrder = p.values.reduce((max, v) => Math.max(max, toInt(v.displayOrder)), 0) + 1;
      return {
        ...p,
        values: [...p.values, {
          key: newRowKey(), id: null, valueCode: '', valueName: '', parentKey: '',
          displayOrder: String(p.values.length ? nextOrder : 1), itemCount: 0,
          dependentLinkCount: 0, allowedUnder: [],
        }],
      };
    });
    clearFieldError('values');
  }, [clearFieldError]);

  const removeRow = useCallback((key: string) => {
    setForm(p => ({
      ...p,
      values: p.values
        .filter(v => v.key !== key)
        .map(v => (v.parentKey === key ? { ...v, parentKey: '' } : v)),
    }));
    setFieldErrors(prev => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([k, msg]) => { if (!k.startsWith(`v:${key}:`)) next[k] = msg; });
      return next;
    });
  }, []);

  const handleHierarchicalChange = useCallback((checked: boolean) => {
    if (checked) { setForm(p => ({ ...p, isHierarchical: true })); return; }
    if (formRef.current.values.some(v => v.parentKey)) { setFlatConfirmOpen(true); return; }
    setForm(p => ({ ...p, isHierarchical: false, leafOnly: false }));
  }, []);

  const confirmFlatten = useCallback(() => {
    setFlatConfirmOpen(false);
    setForm(p => ({ ...p, isHierarchical: false, leafOnly: false, values: p.values.map(v => ({ ...v, parentKey: '' })) }));
    setFieldErrors(prev => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([k, msg]) => { if (!k.endsWith(':parent')) next[k] = msg; });
      return next;
    });
  }, []);

  /** Changing or clearing "Depends on" clears every value's allowed-under list (after a confirm when any has entries). */
  const applyDependsOn = useCallback((id: string) => {
    setForm(p => ({ ...p, dependsOnDimensionId: id, values: p.values.map(v => ({ ...v, allowedUnder: [] })) }));
  }, []);

  const handleDependsOnChange = useCallback((id: string) => {
    if (id === formRef.current.dependsOnDimensionId) return;
    if (formRef.current.values.some(v => v.allowedUnder.length > 0)) { setPendingDependsOn(id); return; }
    applyDependsOn(id);
  }, [applyDependsOn]);

  // --- Validate + save ----------------------------------------------------

  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!form.dimensionCode.trim()) errors.dimensionCode = 'Code is required';
    if (!form.dimensionName.trim()) errors.dimensionName = 'Name is required';

    const byCode = new Map<string, string[]>();
    form.values.forEach(v => {
      const code = v.valueCode.trim();
      if (!code) errors[`v:${v.key}:code`] = 'Code is required';
      else {
        const k = code.toUpperCase();
        byCode.set(k, [...(byCode.get(k) || []), v.key]);
      }
      if (!v.valueName.trim()) errors[`v:${v.key}:name`] = 'Name is required';
    });
    byCode.forEach(keys => {
      if (keys.length > 1) keys.forEach(k => { errors[`v:${k}:code`] = 'Duplicate code'; });
    });
    if (form.isHierarchical) {
      const parentOf = new Map(form.values.map(v => [v.key, v.parentKey]));
      form.values.forEach(v => {
        if (!v.parentKey) return;
        if (v.parentKey === v.key) { errors[`v:${v.key}:parent`] = 'A value cannot be its own parent'; return; }
        // Walk up: reaching this row again means a loop.
        let cursor = v.parentKey;
        for (let i = 0; i < form.values.length && cursor; i++) {
          if (cursor === v.key) { errors[`v:${v.key}:parent`] = 'Parent chain loops back to this value'; break; }
          cursor = parentOf.get(cursor) || '';
        }
      });
    }

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    try {
      const excludeId = mode === 'edit' ? record?.id : undefined;
      const ok = await classificationService.isCodeAvailable(form.dimensionCode.trim(), excludeId);
      if (!ok) { setFieldErrors({ dimensionCode: 'This code already exists.' }); return; }
    } catch { /* enforced server-side */ }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form, mode, record]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const codeByKey = new Map(form.values.map(v => [v.key, v.valueCode.trim()]));
      const values: ClassificationValueInput[] = form.values.map(v => ({
        id: v.id || undefined,
        valueCode: v.valueCode.trim(),
        valueName: v.valueName.trim(),
        parentCode: form.isHierarchical && v.parentKey ? (codeByKey.get(v.parentKey) || null) : null,
        allowedUnderValueIds: form.dependsOnDimensionId ? [...v.allowedUnder] : [],
        displayOrder: toInt(v.displayOrder),
      }));
      const payload: CreateClassificationDimensionRequest = {
        dimensionCode: form.dimensionCode.trim().toUpperCase(),
        dimensionName: form.dimensionName.trim(),
        isHierarchical: form.isHierarchical,
        isRequired: form.isRequired,
        leafOnly: form.isHierarchical && form.leafOnly,
        dependsOnDimensionId: form.dependsOnDimensionId || null,
        displayOrder: toInt(form.displayOrder),
        description: form.description.trim() || null,
        statusCode: form.statusCode,
        values,
      };
      if (mode === 'edit' && record) {
        const rowVersion = detailRef.current?.rowVersion ?? record.rowVersion;
        await classificationService.update(record.id, { ...payload, rowVersion });
      } else {
        await classificationService.create(payload);
      }
      setSaving(false);
      notificationService.toast.success(`Classification "${payload.dimensionName}" saved.`);
      onSaved();
      onClose();
    } catch (err) {
      setSaving(false);
      if (errorStatus(err) === 409) {
        notificationService.toast.error(errorMessage(err,
          'Someone else changed this classification. Close and reopen it to pick up their changes.'));
        onSaved();
        return;
      }
      notificationService.toast.error(errorMessage(err, 'Could not save the classification.'));
    }
  }, [form, mode, record, onSaved, onClose]);

  if (!isOpen) return null;

  const errCls = (f: string, base = 'form-input') => `${base}${fieldErrors[f] ? ' form-field-error' : ''}`;
  const headerText = mode === 'create' ? 'Add Classification' : mode === 'copy' ? 'Copy Classification' : 'Edit Classification';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {saving && <LoadingOverlay text="Saving..." />}
        {!saving && loadingDetail && <LoadingOverlay text="Loading..." />}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{headerText}</h2>
          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving || loadingDetail}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="dimensionCode">
              <label className="form-label">Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={errCls('dimensionCode')}
                value={form.dimensionCode}
                maxLength={30}
                placeholder="e.g. BRAND"
                readOnly={mode === 'edit'}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : undefined}
                onChange={e => { clearFieldError('dimensionCode'); setForm(p => ({ ...p, dimensionCode: e.target.value.toUpperCase() })); }}
              />
              {getFieldError('dimensionCode') && <div className="form-error-message">{getFieldError('dimensionCode')}</div>}
            </div>

            <div className="form-group" data-field="dimensionName">
              <label className="form-label">Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={errCls('dimensionName')}
                value={form.dimensionName}
                maxLength={100}
                placeholder="e.g. Brand"
                onChange={e => { clearFieldError('dimensionName'); setForm(p => ({ ...p, dimensionName: e.target.value })); }}
              />
              {getFieldError('dimensionName') && <div className="form-error-message">{getFieldError('dimensionName')}</div>}
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
                <input type="checkbox" checked={form.isHierarchical}
                  onChange={e => handleHierarchicalChange(e.target.checked)} />
                Hierarchical
              </label>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem', marginLeft: '1.5rem' }}>
                Values can nest under other values
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
                <input type="checkbox" checked={form.isRequired}
                  onChange={e => setForm(p => ({ ...p, isRequired: e.target.checked }))} />
                Required for stock items
              </label>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem', marginLeft: '1.5rem' }}>
                Every stock item must carry a value
              </div>
            </div>

            {form.isHierarchical && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
                  <input type="checkbox" checked={form.leafOnly}
                    onChange={e => setForm(p => ({ ...p, leafOnly: e.target.checked }))} />
                  Leaf values only
                </label>
                <div style={{ ...hintText, marginLeft: '1.5rem' }}>
                  Items can take only values with nothing under them (Apple, not Fruits)
                </div>
              </div>
            )}

            <div className="form-group" data-field="dependsOnDimensionId">
              <SyutSelect
                label="Depends on"
                options={dependsOnOptions}
                value={form.dependsOnDimensionId}
                onChange={val => handleDependsOnChange((val as string) || '')}
                placeholder="Independent"
                noOptionsMessage="No other active classifications"
                isClearable
                menuPortalTarget={document.body}
              />
              <div style={hintText}>
                Values of this classification are limited to chosen values of that one (Sub-category depends on Category)
              </div>
            </div>

            <div className="form-group" data-field="displayOrder">
              <label className="form-label">Display order</label>
              <input type="number" className="form-input" value={form.displayOrder} min={0}
                onChange={e => setForm(p => ({ ...p, displayOrder: e.target.value }))} />
            </div>

            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                options={STATUS_OPTIONS}
                value={form.statusCode}
                onChange={val => setForm(p => ({ ...p, statusCode: (val as string) || 'ACTIVE' }))}
                isClearable={false}
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows={2} value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>

          {/* Values */}
          <div data-field="values" style={{ marginTop: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>Values</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {form.values.length} value{form.values.length === 1 ? '' : 's'}
                  {form.isHierarchical ? ' — pick a parent to nest a value under another' : ''}
                </div>
              </div>
              <button type="button" className="btn btn-secondary" onClick={addRow}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
                <IconSystem.add size={14} /> Add value
              </button>
            </div>

            <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: '20%' }}>Code <span className="text-danger">*</span></th>
                    <th style={thStyle}>Name <span className="text-danger">*</span></th>
                    {form.isHierarchical && <th style={{ ...thStyle, width: '22%' }}>Parent</th>}
                    {form.dependsOnDimensionId && (
                      <th style={{ ...thStyle, width: '26%' }}>Allowed under {dependsOnName}</th>
                    )}
                    <th style={{ ...thStyle, width: 90 }}>Display order</th>
                    <th style={{ ...thStyle, width: 60, textAlign: 'right' }}>Items</th>
                    <th style={{ ...thStyle, width: 44 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.values.length === 0 && (
                    <tr>
                      <td colSpan={5 + (form.isHierarchical ? 1 : 0) + (form.dependsOnDimensionId ? 1 : 0)}
                        style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)', padding: '1rem' }}>
                        No values yet — click Add value.
                      </td>
                    </tr>
                  )}
                  {form.values.map(v => {
                    const codeErr = fieldErrors[`v:${v.key}:code`];
                    const nameErr = fieldErrors[`v:${v.key}:name`];
                    const parentErr = fieldErrors[`v:${v.key}:parent`];
                    // Any other row the user has started is a candidate parent: label it by
                    // code and name so a row with only a name typed so far still shows.
                    const parentOptions: SelectOption[] = form.values
                      .filter(o => o.key !== v.key && (o.valueCode.trim() || o.valueName.trim()))
                      .map(o => {
                        const code = o.valueCode.trim();
                        const name = o.valueName.trim();
                        return { value: o.key, label: code && name ? `${code} - ${name}` : (code || name) };
                      });
                    const inUse = v.itemCount > 0;
                    const linked = v.dependentLinkCount > 0;
                    const removeBlocked = inUse || linked;
                    const removeTitle = inUse
                      ? `Used by ${v.itemCount} item${v.itemCount === 1 ? '' : 's'}`
                      : linked ? 'Values of a dependent classification are allowed under it' : 'Remove value';
                    return (
                      <tr key={v.key}>
                        <td style={tdStyle} data-field={`v:${v.key}:code`}>
                          <input type="text" className={errCls(`v:${v.key}:code`)} value={v.valueCode} maxLength={30}
                            style={cellInput}
                            onChange={e => { clearFieldError(`v:${v.key}:code`); updateRow(v.key, { valueCode: e.target.value }); }} />
                          {codeErr && <div className="form-error-message">{codeErr}</div>}
                        </td>
                        <td style={tdStyle} data-field={`v:${v.key}:name`}>
                          <input type="text" className={errCls(`v:${v.key}:name`)} value={v.valueName} maxLength={150}
                            style={cellInput}
                            onChange={e => { clearFieldError(`v:${v.key}:name`); updateRow(v.key, { valueName: e.target.value }); }} />
                          {nameErr && <div className="form-error-message">{nameErr}</div>}
                        </td>
                        {form.isHierarchical && (
                          <td style={tdStyle} data-field={`v:${v.key}:parent`}>
                            <SyutSelect
                              options={parentOptions}
                              value={v.parentKey}
                              onChange={val => { clearFieldError(`v:${v.key}:parent`); updateRow(v.key, { parentKey: (val as string) || '' }); }}
                              placeholder="Top level"
                              noOptionsMessage="No other values yet: add the parent value as its own row first"
                              isClearable
                              size="sm"
                              menuPortalTarget={document.body}
                              error={parentErr}
                            />
                          </td>
                        )}
                        {form.dependsOnDimensionId && (
                          <td style={tdStyle} data-field={`v:${v.key}:allowedUnder`}>
                            <SyutSelect
                              isMulti
                              options={parentValueOptions}
                              value={v.allowedUnder}
                              onChange={val => updateRow(v.key, { allowedUnder: Array.isArray(val) ? val : (val ? [val] : []) })}
                              placeholder="Any"
                              isLoading={loadingParentValues}
                              noOptionsMessage={loadingParentValues ? 'Loading...' : `${dependsOnName || 'That classification'} has no values yet`}
                              isClearable
                              size="sm"
                              menuPortalTarget={document.body}
                            />
                          </td>
                        )}
                        <td style={tdStyle}>
                          <input type="number" className="form-input" value={v.displayOrder} min={0} style={cellInput}
                            onChange={e => updateRow(v.key, { displayOrder: e.target.value })} />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-secondary)', paddingTop: '0.6rem' }}>
                          {v.id ? v.itemCount : '—'}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <span title={removeTitle}>
                            <button type="button" className="modal-header-btn" disabled={removeBlocked}
                              onClick={() => removeRow(v.key)}
                              style={{ color: removeBlocked ? 'var(--text-muted)' : 'var(--danger, #dc2626)', cursor: removeBlocked ? 'not-allowed' : 'pointer' }}>
                              <IconSystem.trash size={15} />
                            </button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => { void handleSave(); }} disabled={saving || loadingDetail}>
            <IconSystem.check size={16} /> {mode === 'edit' ? 'Save' : 'Create'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`${mode === 'edit' ? 'Save changes to' : 'Create'} classification "${form.dimensionName}" with ${form.values.length} value${form.values.length === 1 ? '' : 's'}?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void handleConfirmSave(); }}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={flatConfirmOpen}
        title="Remove nesting?"
        message="Turning off Hierarchical clears the parent of every value. Continue?"
        confirmLabel="Clear parents"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={confirmFlatten}
        onCancel={() => setFlatConfirmOpen(false)}
      />

      <ConfirmDialog
        open={pendingDependsOn !== null}
        title="Change Depends on?"
        message={'Changing or clearing "Depends on" clears the "Allowed under" choices of every value. Continue?'}
        confirmLabel="Clear choices"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => { const id = pendingDependsOn ?? ''; setPendingDependsOn(null); applyDependsOn(id); }}
        onCancel={() => setPendingDependsOn(null)}
      />

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// VIEW MODAL
// ---------------------------------------------------------------------------

const ViewField: React.FC<{ label: string; value?: string | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{
      padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem',
      border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px', whiteSpace: 'pre-wrap',
    }}>
      {value ? value : <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

interface ClassificationViewModalProps {
  isOpen: boolean;
  record: ClassificationDimensionDto | null;
  onClose: () => void;
  onEdit?: (r: ClassificationDimensionDto) => void;
  tabId?: string;
  moduleName: string;
  onRestoreView?: (code: string) => void;
}

const ClassificationViewModal: React.FC<ClassificationViewModalProps> = ({
  isOpen, record, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();
  const [detail, setDetail] = useState<ClassificationDimensionDto | null>(null);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (!isOpen || !record) { setDetail(null); setLoadingData(false); return; }
    let cancelled = false;
    setLoadingData(true);
    classificationService.getById(record.id)
      .then(d => { if (!cancelled) setDetail(d); })
      .catch(err => { if (!cancelled) notificationService.toast.error(errorMessage(err, 'Could not load the values.')); })
      .finally(() => { if (!cancelled) setLoadingData(false); });
    return () => { cancelled = true; };
  }, [isOpen, record]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !record) return;
    minimize({
      id: `${tabId}-view-modal-${record.dimensionCode}`,
      title: `View - ${record.dimensionCode}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(record.dimensionCode),
    });
    onClose();
  }, [tabId, moduleName, record, minimize, onClose, onRestoreView]);

  if (!isOpen || !record) return null;
  const r = detail ?? record;
  const values = [...(detail?.values || [])].sort((a, b) => a.displayOrder - b.displayOrder || a.valueCode.localeCompare(b.valueCode));
  const hasDependency = !!r.dependsOnDimensionId;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {loadingData && <LoadingOverlay text="Loading..." />}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Classification Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={loadingData}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Code" value={r.dimensionCode} />
            <div>
              <label className="form-label">Status</label>
              <div style={{ paddingTop: '4px' }}>
                <span className="badge" style={r.statusColor ? { background: r.statusColor, color: '#fff' } : undefined}>
                  {r.statusName || r.statusCode || 'Active'}
                </span>
              </div>
            </div>
            <ViewField label="Name" value={r.dimensionName} fullWidth />
            <ViewField label="Hierarchical" value={yesNo(r.isHierarchical)} />
            <ViewField label="Required for stock items" value={yesNo(r.isRequired)} />
            <ViewField label="Leaf values only" value={yesNo(r.isHierarchical && !!r.leafOnly)} />
            <ViewField label="Depends on" value={r.dependsOnDimensionName} />
            <ViewField label="Owner module" value={r.ownerModule || 'Managed here'} />
            <ViewField label="Display order" value={String(r.displayOrder ?? 0)} />
            <ViewField label="Values" value={String(detail ? values.length : r.valueCount)} />
            <ViewField label="Description" value={r.description} fullWidth />

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Values</label>
              <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Code</th>
                      <th style={thStyle}>Name</th>
                      {r.isHierarchical && <th style={thStyle}>Parent</th>}
                      {hasDependency && <th style={thStyle}>Allowed under</th>}
                      <th style={{ ...thStyle, textAlign: 'right' }}>Display order</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {values.length === 0 && (
                      <tr>
                        <td colSpan={4 + (r.isHierarchical ? 1 : 0) + (hasDependency ? 1 : 0)}
                          style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>
                          {loadingData ? 'Loading…' : 'No values.'}
                        </td>
                      </tr>
                    )}
                    {values.map(v => (
                      <tr key={v.id}>
                        <td style={tdStyle}>{v.valueCode}</td>
                        <td style={tdStyle}>{v.valueName}</td>
                        {r.isHierarchical && (
                          <td style={{ ...tdStyle, color: v.parentCode ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                            {v.parentCode ? `${v.parentCode}${v.parentName ? ` - ${v.parentName}` : ''}` : 'Top level'}
                          </td>
                        )}
                        {hasDependency && (
                          <td style={{ ...tdStyle, color: (v.allowedUnder || []).length ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                            {(v.allowedUnder || []).length ? v.allowedUnder.map(a => a.valueName).join(', ') : 'Any'}
                          </td>
                        )}
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{v.displayOrder}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{v.itemCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <ViewField label="Created By" value={r.createdByName} />
            <ViewField label="Created At" value={r.createdAt ? new Date(r.createdAt).toLocaleString() : null} />
            {r.updatedAt && (
              <>
                <ViewField label="Updated By" value={r.updatedByName} />
                <ViewField label="Updated At" value={new Date(r.updatedAt).toLocaleString()} />
              </>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && !r.ownerModule && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(record); }}>
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// SCREEN
// ---------------------------------------------------------------------------

type ViewMode = 'list' | 'grid';

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'dimensionCode', label: 'Code' },
  { id: 'dimensionName', label: 'Name' },
  { id: 'valueCount', label: 'Values' },
  { id: 'isHierarchical', label: 'Hierarchical' },
  { id: 'isRequired', label: 'Required for stock items' },
  { id: 'dependsOn', label: 'Depends on' },
  { id: 'displayOrder', label: 'Display order' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface ItemClassificationScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ItemClassificationScreen: React.FC<ItemClassificationScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('item-classification');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();
  const screenName = moduleName || 'Item Classifications';

  const [queryParams, setQueryParams] = useState<ClassificationDimensionQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useClassifications(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('item-classification-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [selected, setSelected] = useState<ClassificationDimensionDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<ClassificationDimensionDto | null>(null);

  const handleAdd = useCallback(() => {
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelected(null); setFormOpen(true);
  }, [tabId, isMinimized]);

  const handleEdit = useCallback((r: ClassificationDimensionDto) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.dimensionCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((r: ClassificationDimensionDto) => {
    const modalId = tabId ? `${tabId}-form-modal-copy-${r.dimensionCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    if (formOpen) formModalMinimizeRef.current?.();
    setFormMode('copy'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal, formOpen]);

  const handleView = useCallback((r: ClassificationDimensionDto) => {
    const viewId = tabId ? `${tabId}-view-modal-${r.dimensionCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setViewRecord(r); setViewOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((code: string) => {
    const rec = data.find(r => r.dimensionCode === code);
    if (rec) { setViewRecord(rec); setViewOpen(true); }
  }, [data]);

  const handleRestoreModal = useCallback(
    (m: FormMode, instanceId?: string, recordCode?: string) => {
      if (m === 'create' && instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(r => r.dimensionCode === recordCode);
        if (rec) setSelected(rec);
      }
      setFormMode(m); setFormOpen(true);
    },
    [data],
  );

  const handleDelete = useCallback(async (r: ClassificationDimensionDto) => {
    const ok = await confirmDelete({ resourceLabel: r.dimensionName });
    if (!ok) return;
    try {
      await classificationService.delete(r.id, r.rowVersion);
      notificationService.toast.success(`Classification "${r.dimensionName}" deleted.`);
      refetch();
    } catch (err) {
      // The API refuses while items carry one of its values, while other classifications
      // depend on it (dependentDimensionCount > 0), or on a stale rowVersion (409).
      notificationService.toast.error(errorMessage(err, 'Could not delete.'));
      if (errorStatus(err) === 409) refetch();
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<ClassificationDimensionDto>[] = useMemo(() => [
    { id: 'dimensionCode', header: 'Code', accessorKey: 'dimensionCode', size: 140 },
    { id: 'dimensionName', header: 'Name', accessorKey: 'dimensionName', size: 260,
      cell: ({ row }) => {
        const r = row.original;
        return (
          <span>
            {r.dimensionName}
            {r.ownerModule && <OwnerBadge module={r.ownerModule} />}
          </span>
        );
      } },
    { id: 'valueCount', header: 'Values', accessorKey: 'valueCount', size: 90, align: 'right',
      cell: ({ row }) => row.original.valueCount ?? 0 },
    { id: 'isHierarchical', header: 'Hierarchical', accessorKey: 'isHierarchical', size: 120,
      cell: ({ row }) => yesNo(row.original.isHierarchical) },
    { id: 'isRequired', header: 'Required for stock items', accessorKey: 'isRequired', size: 190,
      cell: ({ row }) => yesNo(row.original.isRequired) },
    { id: 'dependsOn', header: 'Depends on', accessorKey: 'dependsOnDimensionName', size: 160,
      cell: ({ row }) => row.original.dependsOnDimensionName || '—' },
    { id: 'displayOrder', header: 'Display order', accessorKey: 'displayOrder', size: 120, align: 'right' },
    {
      id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110,
      cell: ({ row }) => {
        const r = row.original;
        return (
          <span className="badge" style={r.statusColor ? { background: r.statusColor, color: '#fff' } : undefined}>
            {r.statusName || 'Active'}
          </span>
        );
      },
    },
  ], []);

  const rowActions = useCallback((r: ClassificationDimensionDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    if (can('CREATE')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
    }
    // A classification maintained by a domain module is read-only here.
    const managedHere = !r.ownerModule;
    if (managedHere && can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
    }
    if (managedHere && can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => { void handleDelete(r); }, variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'Add Classification'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const HeaderIcon: IconComponent = (moduleIcon && ICONS[moduleIcon]) || IconSystem.list;

  return (
    <EnterpriseScreenShell
      title="Item Classifications"
      description="Your own ways of grouping items — Brand, Division, Sub-category — each with its list of values"
      icon={<HeaderIcon size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search classifications...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount: queryParams.includeInactive ? 1 : 0,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={e => setIncludeInactive(e.target.checked)} /> Include inactive
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setIncludeInactive(false);
                setQueryParams(p => ({ page: 1, pageSize: p.pageSize, searchTerm: p.searchTerm }));
              }}>Clear</button>
              <button className="btn btn-primary" onClick={() => {
                setQueryParams(p => ({ ...p, includeInactive: includeInactive || undefined, page: 1 }));
                setFilterOpen(false);
              }}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No classifications yet — click Add.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{r.dimensionCode}</div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0', color: 'var(--text-primary)' }}>
                        {r.dimensionName}
                        {r.ownerModule && <OwnerBadge module={r.ownerModule} />}
                      </h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {r.valueCount ?? 0} value{(r.valueCount ?? 0) === 1 ? '' : 's'}
                    {r.isHierarchical ? ' · Hierarchical' : ''}
                    {r.isRequired ? ' · Required' : ''}
                    {r.dependsOnDimensionName ? ` · Depends on ${r.dependsOnDimensionName}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ClassificationFormModal isOpen={formOpen} mode={formMode} record={selected}
        onSaved={refetch}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={screenName}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal} />

      <ClassificationViewModal isOpen={viewOpen} record={viewRecord}
        onClose={() => { setViewOpen(false); setViewRecord(null); }}
        onEdit={can('UPDATE') ? handleEdit : undefined}
        tabId={tabId} moduleName={screenName}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default ItemClassificationScreen;
